/**
 * #91 — the warning row for a picker holding a database the server did not
 * list. Renders nothing when there is nothing to say.
 *
 * Sits NEXT TO the field, not inside its dropdown, for the reason Soporte C2
 * gave for the listing-failure banner: a user does not open a closed combobox
 * to find out why it looks wrong. It is also the only surface that works in
 * both of the field's render modes — `SchemaCompareCompactField` collapses to
 * a plain text affordance once a value is set, and that mode shows the stale
 * name with no `?` indicator and no dropdown at all.
 *
 * The predicate and the wording both live in `utils/unlistedDatabase.ts`; this
 * component only decides where it appears.
 */

import { AlertTriangle } from 'lucide-react';
import { useConnectionStore } from '../../connections/store/connectionStore';
import {
  unlistedDatabaseMessage,
  unlistedDatabaseReason,
  type UnlistedDatabaseInput,
} from '../utils/unlistedDatabase';
import type { DatabasesListingState } from '../../database-objects/services/databasesService';

type UnlistedDatabaseNoticeProps = {
  connectionId: string | undefined;
  database: string | undefined;
  databases: readonly string[] | undefined;
  state: DatabasesListingState | undefined;
  isLoading?: boolean;
  /** Pickers should pass a stable prefix so the testid is scoped. */
  testIdPrefix: string;
};

export function UnlistedDatabaseNotice({
  connectionId,
  database,
  databases,
  state,
  isLoading,
  testIdPrefix,
}: UnlistedDatabaseNoticeProps) {
  const connections = useConnectionStore((s) => s.connections);
  const activeConnections = useConnectionStore((s) => s.activeConnections);

  // Saved connection wins, live one as fallback — the same resolution order
  // `useFallbackPresentation` and `getLimitedConnectionDatabase` use, so the
  // "Limit connection to…" read-back cannot disagree with the listing filter
  // that produced the shortened list in the first place.
  const connection: UnlistedDatabaseInput['connection'] = connectionId
    ? (connections.find((c) => c.id === connectionId) ??
      activeConnections.find((c) => c.id === connectionId)?.connection)
    : undefined;

  const reason = unlistedDatabaseReason({ database, databases, state, isLoading, connection });
  if (!reason || !database) return null;

  return (
    <div
      className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400"
      data-testid={`${testIdPrefix}-unlisted-database-notice`}
      data-unlisted-reason={reason}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{unlistedDatabaseMessage(reason, database, connection)}</span>
    </div>
  );
}
